import { CARCASS_MM } from "./domain/carcass";
import type {
  FacadeColorType,
  FacadePatternType,
  FacadeType,
  HandleType,
  ItemType,
  MaterialType,
  ModuleType
} from "./types";

export const MATERIALS: Record<
  MaterialType,
  { name: string; subtitle: string; color: string; grain: string }
> = {
  warmOak: {
    name: "Дуб медовый",
    subtitle: "Натуральная тёплая текстура",
    color: "#baa27f",
    grain: "#78664f"
  },
  linen: {
    name: "Лён песочный",
    subtitle: "Спокойная тканевая фактура",
    color: "#d8c7aa",
    grain: "#8d795d"
  },
  matteWhite: {
    name: "Белый шёлк",
    subtitle: "Ровная шелковисто-матовая поверхность",
    color: "#faf9f6",
    grain: "#faf9f6"
  },
  walnut: {
    name: "Орех табачный",
    subtitle: "Тёплый ровный рисунок ореха",
    color: "#754722",
    grain: "#402512"
  },
  graphite: {
    name: "Графит",
    subtitle: "Бархатистый супермат",
    color: "#30302e",
    grain: "#141413"
  }
};

export const FACADES: Record<
  FacadeType,
  { name: string; subtitle: string }
> = {
  wood: {
    name: "ЛДСП в декоре",
    subtitle: "Гладкий плитный фасад из демонстрационной коллекции"
  },
  glass: {
    name: "Стекло в алюминиевом профиле",
    // Профилей и стёкол слишком много, чтобы выбирать их в конструкторе.
    subtitle: "Цвет профиля и стекла подбираются с менеджером"
  },
  satin: {
    name: "МДФ в эмали",
    subtitle: "Гладкий или с настоящей 3D-фрезеровкой"
  },
  mirror: {
    name: "Зеркальный фасад",
    subtitle: "Полноразмерное зеркало на несущем фасаде"
  },
  gloss: {
    name: "МДФ в эмали с фрезеровкой",
    subtitle: "Совместимость со старыми сохранёнными проектами"
  }
};

export const FACADE_COLORS: Record<
  FacadeColorType,
  { name: string; subtitle: string; color: string }
> = {
  white: {
    name: "Белая эмаль",
    subtitle: "Нейтральный мягкий белый",
    color: "#e7e5df"
  },
  cashmere: {
    name: "Кашемир",
    subtitle: "Тёплый серо-бежевый",
    color: "#c8bbab"
  },
  sage: {
    name: "Шалфей",
    subtitle: "Спокойный приглушённый зелёный",
    color: "#718071"
  },
  graphite: {
    name: "Графит",
    subtitle: "Глубокий тёмно-серый",
    color: "#343634"
  }
};

export const FACADE_PATTERNS: Record<
  FacadePatternType,
  { name: string; subtitle: string }
> = {
  plain: {
    name: "Гладкий фасад",
    subtitle: "Чистая ровная поверхность без фрезеровки"
  },
  art001: {
    name: "ART.001",
    subtitle: "Частая вертикальная фрезеровка"
  },
  art005: {
    name: "ART.005",
    subtitle: "Вертикальный верх и горизонтальный низ"
  },
  art006: {
    name: "ART.006",
    subtitle: "Ритмичный горизонтальный шеврон"
  },
  art007: {
    name: "ART.007",
    subtitle: "Объёмный рисунок в форме чешуи"
  },
  art008: {
    name: "ART.008",
    subtitle: "Группы тонких диагональных линий"
  },
  art009: {
    name: "ART.009",
    subtitle: "Тонкая минималистичная рамка"
  },
  art010: {
    name: "ART.010",
    subtitle: "Изометрический объёмный рисунок кубами"
  },
  art012: {
    name: "ART.012",
    subtitle: "Многоуровневая классическая рамка"
  },
  art023: {
    name: "ART.023",
    subtitle: "Широкая фасетная рамка с утопленной филёнкой"
  },
  art036: {
    name: "ART.036",
    subtitle: "Объёмная геометрия в форме сот"
  },
  art037: {
    name: "ART.037",
    subtitle: "Частая ромбовидная фрезеровка"
  }
};

/**
 * Модель ручки в конструкторе не выбирается: их слишком много и они всё время
 * меняются в продаже. Клиент выбирает только «ручка» или «без ручки», а саму
 * ручку подбирает с менеджером или покупает сам — сборщик ставит её на месте.
 * Профиль и врезная остались в типе ради старых проектов и при загрузке
 * становятся обычной ручкой.
 */
export const HANDLES: Record<HandleType, { name: string }> = {
  bar: { name: "Ручка" },
  profile: { name: "Профиль C" },
  recessed: { name: "Врезная" },
  // Не «фрезерованная»: выборка под пальцы на кромке фасада, лицо гладкое (Сергей, 22.10).
  integrated: { name: "Интегрированная" },
  push: { name: "Без ручки · Tip-On" },
  // Зацеп — часть профиля стеклянного фасада, отдельно не покупается.
  glassGrip: { name: "Ручка интегрированная (в профиле)" }
};

/** Вид интегрированной ручки — выбирает покупатель (приёмка 22.10). */
export const INTEGRATED_HANDLE_STYLES: Record<"edge" | "edgePart" | "top" | "topPart", { name: string; short: string }> = {
  edge: { name: "По всей высоте, на торце", short: "по всей высоте двери" },
  edgePart: { name: "Участок на торце", short: "на участке на высоте руки" },
  top: { name: "По верхнему краю", short: "по верхнему краю" },
  topPart: { name: "Участок по центру", short: "по верхнему краю, участок по центру" }
};

/** Что предлагается клиенту. Выборку делает цех, это не покупная модель. */
export const HANDLE_CHOICES: readonly HandleType[] = ["bar", "push", "integrated", "glassGrip"];

/**
 * Выбор ручки по фасаду. У стекла — только зацеп в профиле или Tip-On: скобу на
 * стекло не ставят (решение Сергея 28.09). Фрезерованная — только у МДФ.
 */
export const handleChoicesForFacade = (facade: FacadeType | undefined): HandleType[] =>
  facade === "glass" ? ["glassGrip", "push"]
    : facade === "satin" || facade === "gloss" ? ["bar", "push", "integrated"]
      : ["bar", "push"];

/** Ручка, которая допустима для фасада; недопустимая заменяется ближайшей. */
export const handleForFacade = (handle: HandleType, facade: FacadeType | undefined): HandleType =>
  handleChoicesForFacade(facade).includes(handle)
    ? handle
    : facade === "glass" ? "glassGrip" : "bar";

/** Старые варианты, которых больше нет в выборе, превращаются в обычную ручку. */
export const normalizeHandleChoice = (value: unknown): HandleType =>
  value === "push" || value === "integrated" || value === "glassGrip" ? value : "bar";

/**
 * Стеклянная полка в ячейке стеллажа. Толщина — SHELVING_GLASS_SHELF_THICKNESS_MM.
 * Цены стекла и полкодержателей — в прайсе (кабинет → «Цены»).
 */
export const GLASS_SHELF = {
  name: "Полка стеклянная"
} as const;

/** Зеркало 4 мм, наклеенное на открытую боковину шкафа (кромка обработана). Цена — в прайсе. */
export const SIDE_MIRROR = {
  name: "Зеркало на боковине"
} as const;

export const INTERNALS: Record<
  ItemType,
  { name: string; short: string; defaultY: number; height?: number }
> = {
  shelf: { name: "Полка", short: `ЛДСП ${CARCASS_MM} мм`, defaultY: 900 },
  drawer: {
    name: "Выдвижной ящик",
    short: "Полное выдвижение",
    defaultY: 352,
    // Не ниже 250 мм: направляющая и дно съедают часть высоты (правило цеха).
    height: 250
  },
  rail: { name: "Штанга", short: "Хром · Ø25 мм", defaultY: 1650 },
  basket: {
    name: "Сетчатая корзина",
    short: "Выдвижная · пенал 600–900 мм",
    defaultY: 320,
    height: 160
  },
  leatherBasket: {
    name: "Корзина «под кожу»",
    short: "Эко-кожа · пенал 600–900 мм",
    defaultY: 480,
    height: 160
  },
  // Деревянная полка с бортиком по лицу (замечание № 26); высоты у неё нет — только бортик.
  shoeRack: {
    name: "Обувница",
    short: "Полка с бортиком 40 мм",
    defaultY: 320
  },
  trouserRack: {
    name: "Брючница",
    short: "Выдвижная · пенал 600–900 мм",
    defaultY: 1184,
    height: 450
  },
  pantograph: {
    name: "Пантограф",
    short: "Опускаемая штанга",
    defaultY: 1900
  },
  divider: {
    name: "Перегородка",
    short: `Вертикальная, ЛДСП ${CARCASS_MM} мм`,
    defaultY: 1100
  },
  led: { name: "LED-подсветка", short: "Тёплый свет", defaultY: 2200 }
};

export const MODULE_NAMES: Record<ModuleType, string> = {
  single: "Одностворчатый пенал",
  double: "Широкий пенал",
  mezzanine: "Антресоль",
  corner45: "Угловой шкаф",
  end: "Торцевой модуль",
  shelving: "Стеллаж"
};

/**
 * Название изделия для экранов и документов: комод собран движком стеллажа,
 * но называется комодом; стеллаж над стеллажом — «Антресоль стеллажа» (11.10).
 */
export const moduleName = (module: { type: ModuleType; shelvingKind?: "chest"; stackedOnId?: string }) =>
  module.type === "shelving" && module.shelvingKind === "chest" ? "Комод"
    : module.type === "shelving" && module.stackedOnId ? "Антресоль стеллажа"
      : MODULE_NAMES[module.type];

export const WALL_COLORS = [
  "#f2eee7",
  "#f7f5f0",
  "#e8dfd1",
  "#d8d2c8",
  "#dfd6ca",
  "#cfc2b1",
  "#f4efe7",
  "#e8e4dc",
  "#d6d0c6",
  "#ece1d3",
  "#ddd7ce",
  "#c9b9a4"
];
